import { getStorageToken } from 'src/utils/auth.storage';
import { sttTtsApi } from './services';

type RealtimeTranscriptionError = {
  message: string;
  type?: string;
};

const createError = (
  message: string,
  type: string = 'transcription_error'
): RealtimeTranscriptionError => ({
  message,
  type,
});

export interface RealtimeTranscriptionReturn {
  start: () => Promise<void>;
  stop: () => void;
  startListening: () => Promise<void>;
  stopListening: () => void;
  enableStreaming: () => void;
  disableStreaming: () => void;
  isRecording: boolean;
}

export interface TranscriptionConfig {
  language?: string;
  prompt?: string;
  response_format?: 'json' | 'text' | 'srt' | 'verbose_json' | 'vtt';
  temperature?: number;
}

interface TranscriptionEvent {
  type: string;
  transcript?: string;
  is_final?: boolean;
  error?: {
    message: string;
    type: string;
  };
}

const defaultConfig: TranscriptionConfig = {
  language: 'en',
  response_format: 'verbose_json',
  temperature: 0,
};

interface RealtimeTranscriptionOptions extends Partial<TranscriptionConfig> {
  onRtcConnected?: () => void;
}

const createSilentAudioTrack = async () => {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  // Context starts in suspended state
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  gainNode.gain.value = 0; // Silent
  oscillator.connect(gainNode);
  const destination = ctx.createMediaStreamDestination();
  gainNode.connect(destination);
  oscillator.start();

  // Resume the audio context if it's suspended
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch (err) {
      console.error('Failed to resume audio context:', err);
      throw createError(
        'Audio initialization failed. Please interact with the page first.',
        'audio_error'
      );
    }
  }

  return destination.stream.getAudioTracks()[0];
};

export function createRealtimeTranscription(
  onTranscription: (text: string, isFinal: boolean) => void,
  onError: (error: RealtimeTranscriptionError) => void,
  userConfig: RealtimeTranscriptionOptions = {}
): RealtimeTranscriptionReturn {
  const config: TranscriptionConfig = { ...defaultConfig, ...userConfig };
  let mediaStream: MediaStream | null = null;
  let pc: RTCPeerConnection | null = null;
  let isRecording = false;
  let dummyTrack: MediaStreamTrack | null = null;
  let dataChannel: RTCDataChannel | null = null;

  const startListening = async () => {
    try {
      // Stop any existing dummy track
      if (dummyTrack) {
        dummyTrack.stop();
        dummyTrack = null;
      }

      // Get real microphone access
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
        video: false,
      });

      // Replace track if connection exists
      if (pc && pc.connectionState === 'connected') {
        const audioTrack = mediaStream.getAudioTracks()[0];
        const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
        if (sender && audioTrack) {
          await sender.replaceTrack(audioTrack);
        }
      }
    } catch (err) {
      console.error('Microphone access error:', err);
      onError(
        createError(
          'Failed to access microphone. Please check your audio settings.',
          'audio_error'
        )
      );
      throw err;
    }
  };

  const stopListening = async () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      mediaStream = null;
    }

    try {
      // Create new dummy track
      dummyTrack = await createSilentAudioTrack();

      // Replace track if connection exists
      if (pc && pc.connectionState === 'connected') {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
        if (sender && dummyTrack) {
          await sender.replaceTrack(dummyTrack);
        }
      }
    } catch (err) {
      console.error('Error creating silent track:', err);
      onError(
        createError(
          'Failed to initialize audio. Please interact with the page first.',
          'audio_error'
        )
      );
    }
  };

  const enableStreaming = () => {
    if (mediaStream) {
      mediaStream.getAudioTracks().forEach((t) => (t.enabled = true));
    }
  };

  const disableStreaming = () => {
    if (mediaStream) {
      mediaStream.getAudioTracks().forEach((t) => (t.enabled = false));
    }
  };

  const start = async () => {
    if (isRecording) {
      console.warn('Transcription already in progress');
      return;
    }

    try {
      // Validate environment
      if (!navigator.mediaDevices || !window.RTCPeerConnection) {
        throw createError(
          'Speech recognition is not supported in this browser.',
          'unsupported_browser'
        );
      }

      // Get OpenAI token
      let ephemeralKey: string;
      try {
        const token = await getStorageToken();
        if (!token) {
          throw createError(
            'Failed to get authentication token',
            'authentication_error'
          );
        }
        const tokenResponse = await sttTtsApi.getOpenAIToken();
        if (
          tokenResponse.kind === 'ok' &&
          'data' in tokenResponse &&
          tokenResponse.data?.token
        ) {
          ephemeralKey = tokenResponse.data.token;
        } else {
          throw createError(
            'Failed to get authentication token',
            'authentication_error'
          );
        }
      } catch (error) {
        throw createError(
          'Failed to connect to the speech service.',
          'connection_error'
        );
      }

      // Create peer connection
      pc = new RTCPeerConnection({ iceServers: [] });

      // Always get a real mic stream for initial RTC connection
      let initialMicStream: MediaStream | null = null;
      try {
        initialMicStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 16000,
          },
          video: false,
        });
        const micTrack = initialMicStream.getAudioTracks()[0];
        pc.addTrack(micTrack, initialMicStream);
      } catch (err) {
        throw createError(
          'Failed to access microphone for initial connection.',
          'audio_error'
        );
      }

      // Set up data channel
      dataChannel = pc.createDataChannel('oai-events');

      // Create and send offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: false,
        offerToReceiveVideo: false,
      });
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering
      await new Promise<void>((resolve) => {
        if (pc?.iceGatheringState === 'complete') {
          resolve();
        } else {
          const handler = () => {
            if (pc?.iceGatheringState === 'complete') {
              pc.removeEventListener('icegatheringstatechange', handler);
              resolve();
            }
          };
          pc?.addEventListener('icegatheringstatechange', handler);
        }
      });

      // Send offer to API
      const apiResponse = await fetch(
        `https://api.openai.com/v1/realtime?intent=transcription`,
        {
          method: 'POST',
          body: pc.localDescription?.sdp,
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            'Content-Type': 'application/sdp',
          },
        }
      );

      if (!apiResponse.ok) {
        throw createError(
          'Failed to connect to the speech service.',
          'api_error'
        );
      }

      // Set remote description
      const answer = await apiResponse.text();
      await pc.setRemoteDescription({
        type: 'answer',
        sdp: answer,
      });

      // Immediately replace mic track with dummy and stop mic stream
      try {
        dummyTrack = await createSilentAudioTrack();
        const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
        if (sender && dummyTrack) {
          await sender.replaceTrack(dummyTrack);
        }
        if (initialMicStream) {
          initialMicStream.getTracks().forEach((track) => track.stop());
        }
      } catch (err) {
        console.warn('Failed to replace mic with dummy after RTC setup', err);
      }
      // Set up data channel message handler
      dataChannel.onmessage = (event: MessageEvent) => {
        try {
          const data: TranscriptionEvent = JSON.parse(event.data);
          if (data.error) {
            throw createError(data.error.message, data.error.type);
          }
          if (data.transcript !== undefined) {
            onTranscription(data.transcript, data.is_final || false);
          }
        } catch (error) {
          console.error('Error processing transcription:', error);
          onError(error instanceof Error ? error : new Error(String(error)));
        }
      };

      // Set up connection state handlers
      pc.onconnectionstatechange = () => {
        const state = pc?.connectionState;
        if (state === 'connected') {
          isRecording = true;
          userConfig.onRtcConnected?.();
        } else if (state === 'failed' || state === 'disconnected') {
          onError(createError(`Connection ${state}.`, 'connection_error'));
          stop();
        }
      };
    } catch (error) {
      console.error('Error starting transcription:', error);
      onError(error as RealtimeTranscriptionError);
      stop();
    }
  };

  const stop = () => {
    if (!isRecording) return;
    isRecording = false;

    if (pc) {
      pc.close();
      pc = null;
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      mediaStream = null;
    }

    if (dummyTrack) {
      dummyTrack.stop();
      dummyTrack = null;
    }

    dataChannel = null;
  };

  return {
    start,
    stop,
    startListening,
    stopListening,
    enableStreaming,
    disableStreaming,
    get isRecording() {
      return isRecording;
    },
  };
}
